<template>
  <div class="inline-flex">
    <o-button flat size="sm" :disabled="disabled" @click="open">
      {{ $t('service.addService') }}
    </o-button>

    <o-modal
      ref="modal"
      :title="$t('service.addService')"
      modal-class="relative m-2 bg-white dark:bg-gray-900 rounded-lg shadow-lg pointer-events-auto overflow-hidden sm:max-w-md sm:w-full h-95vh max-h-[640px]"
      content-class="w-full flex flex-col"
      @open="handleOpen"
    >
      <template #body>
        <div class="flex flex-col flex-grow flex-shrink h-10 overflow-hidden">
          <div class="w-full flex items-center border-b flex-shrink-0">
            <o-icon icon="search" class="text-gray-500 dark:text-gray-400 ml-4" :size="14" />
            <input
              ref="search"
              v-model="query"
              type="text"
              :placeholder="$t('general.search')"
              class="py-3 px-3 w-full text-sm bg-white dark:bg-gray-900 focus:outline-none"
            >
            <button
              v-if="query"
              type="button"
              class="mr-4 flex-shrink-0 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
              :title="$t('general.clear')"
              @click="clearQuery"
            >
              <o-icon icon="x" :size="12" />
            </button>
          </div>

          <div v-if="presetEmployee" class="flex items-center justify-between gap-2 px-4 py-2 bg-blue-50 border-b text-xs text-blue-800 dark:bg-blue-900/5 dark:text-blue-100 dark:border-gray-800 flex-shrink-0">
            <p class="min-w-0">
              <span class="font-medium">{{ presetEmployeeName }}</span>
              {{ $t('components.reservation.create.servicePicker.presetEmployeeInfo') }}
            </p>
            <o-button
              size="xs"
              flat
              icon="x"
              :title="$t('components.reservation.create.servicePicker.presetEmployeeClear')"
              @click="$emit('clear-preset-employee')"
            />
          </div>

          <o-scroll class="flex-grow">
            <div v-if="!grouped.length" class="p-6 text-center text-sm text-gray-600 dark:text-gray-300">
              {{ $t('components.fields.listBox.noOptions') }}
            </div>

            <div v-for="group in grouped" :key="group.idServiceCategory">
              <p v-if="group.label" class="text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wider mt-3 mb-1 px-4">
                {{ group.label }}
              </p>
              <ul>
                <template v-for="service in group.services">
                  <o-tooltip
                    v-if="!isAllowed(service)"
                    :key="service.idService"
                    :text="$t('components.reservation.create.servicePicker.serviceNotEnabledForEmployee', { employee: presetEmployeeName })"
                  >
                    <li
                      class="select-none flex items-center justify-between py-2 px-4 text-sm opacity-50 cursor-not-allowed"
                    >
                      <div class="flex items-center min-w-0">
                        <o-avatar :items="[service]" class="hidden md:flex pr-2 flex-shrink-0" />
                        <div class="min-w-0">
                          <p class="line-clamp-1">
                            {{ service.title }}
                          </p>
                          <p v-if="query && service.category?.name" class="text-xs opacity-70 line-clamp-1">
                            {{ service.category.name }}
                          </p>
                        </div>
                      </div>
                    </li>
                  </o-tooltip>

                  <li
                    v-else
                    :key="service.idService"
                    class="select-none flex items-center justify-between py-2 px-4 transition-colors text-sm"
                    :class="[
                      isSelected(service)
                        ? 'bg-primary-50 text-primary-700 hover:bg-primary-100 dark:bg-primary-800/20 dark:text-primary-300 dark:hover:bg-primary-900/50'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-100',
                      isSelected(service) ? '' : 'cursor-pointer',
                    ]"
                    @click="!isSelected(service) && handleAdd(service)"
                  >
                    <div class="flex items-center min-w-0">
                      <o-avatar :items="[service]" class="hidden md:flex pr-2 flex-shrink-0" />
                      <div class="min-w-0">
                        <p class="line-clamp-1">
                          {{ service.title }}
                        </p>
                        <p v-if="query && service.category?.name" class="text-xs opacity-70 line-clamp-1">
                          {{ service.category.name }}
                        </p>
                      </div>
                    </div>

                    <div
                      v-if="isSelected(service)"
                      class="ml-2 flex-shrink-0 flex items-center border rounded bg-white dark:bg-gray-900 dark:border-gray-700"
                    >
                      <button
                        class="w-6 h-6 flex items-center justify-center text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-l transition-colors"
                        @click.stop="handleRemove(service)"
                      >
                        <o-icon icon="subtract" :size="10" />
                      </button>
                      <span class="min-w-[1.5rem] px-1 text-center text-xs font-medium text-primary-700 dark:text-primary-300">
                        {{ selectedCount(service) }}
                      </span>
                      <button
                        class="w-6 h-6 flex items-center justify-center text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-r transition-colors"
                        @click.stop="handleAdd(service)"
                      >
                        <o-icon icon="add" :size="10" />
                      </button>
                    </div>

                    <div
                      v-else
                      class="ml-2 flex-shrink-0 w-6 h-6 rounded border border-dashed border-gray-300 dark:border-gray-800 flex items-center justify-center text-gray-400 dark:text-gray-500"
                    >
                      <o-icon icon="add" :size="10" />
                    </div>
                  </li>
                </template>
              </ul>
            </div>
          </o-scroll>
        </div>
      </template>

      <template #footer>
        <div class="flex items-center justify-between w-full">
          <p class="text-xs text-gray-600 dark:text-gray-300">
            {{ $t('components.reservation.create.servicePicker.selectedCount', { count: selectedIds.length }, selectedIds.length) }}
          </p>
          <o-button size="sm" variant="primary" @click="close">
            {{ $t('general.done') }}
          </o-button>
        </div>
      </template>
    </o-modal>
  </div>
</template>

<script>
import Fuse from 'fuse.js'

import {
  OAvatar,
} from '@/components/elements'
import { useServiceStore } from '~/stores/useServiceStore'

export default {
  components: {
    OAvatar,
  },
  props: {
    selectedIds: {
      type: Array,
      default: () => [],
    },
    type: {
      type: String,
      default: null,
      validator: value => ['single', 'group'].includes(value),
    },
    addOnMode: {
      type: Array,
      default: null,
    },
    allowInternet: {
      type: Boolean,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    presetEmployee: {
      type: Object,
      default: null,
    },
  },
  data () {
    return {
      query: ''
    }
  },
  computed: {
    services () {
      return useServiceStore().all
    },
    available () {
      const type = this.type
      const addOnMode = this.addOnMode

      return this.services.filter((service) => {
        if (type === 'group' && !service.isServiceCustomerTypeGroup) return false
        if (type === 'single' && !service.isServiceCustomerTypeSingle) return false
        if (addOnMode && !addOnMode.includes(service.addOnMode)) return false
        if (this.allowInternet === true && !service.allowInternet) return false
        return true
      })
    },
    searched () {
      if (!this.query.length) {
        return this.available
      }
      const fuse = new Fuse(this.available, {
        keys: ['title', 'information', 'category.name'],
        distance: 200,
        threshold: 0.35,
      })
      return fuse.search(this.query).map(({ item }) => item)
    },
    grouped () {
      // When searching, render a flat list (no category headings) so results
      // aren't fragmented across groups.
      if (this.query.length) {
        return [{
          idServiceCategory: 0,
          label: '',
          order: 0,
          services: this.searched,
        }]
      }

      const groups = {}
      for (const service of this.searched) {
        const category = service.category
        const id = category?.idServiceCategory || 0
        if (!groups[id]) {
          groups[id] = {
            idServiceCategory: id,
            label: category ? category.name : '',
            order: category ? category.listOrder : 0,
            services: [],
          }
        }
        groups[id].services.push(service)
      }

      return Object.values(groups)
        .sort((a, b) => a.order - b.order)
        .map(group => ({
          ...group,
          services: group.services.slice().sort((a, b) => (a.listOrder || 0) - (b.listOrder || 0)),
        }))
    },
    presetEmployeeName () {
      const employee = this.presetEmployee
      if (!employee) return ''
      return employee.displayName || employee.name || employee.firstName || ''
    },
  },
  methods: {
    isSelected (service) {
      return this.selectedIds.includes(service.idService)
    },
    isAllowed (service) {
      const employee = this.presetEmployee
      if (!employee) return true
      const id = employee.idEmployee ?? employee.id
      if (id == null) return true
      return Array.isArray(service.employeeIDs) && service.employeeIDs.includes(id)
    },
    selectedCount (service) {
      const id = service.idService
      return this.selectedIds.filter(selectedId => selectedId === id).length
    },
    handleAdd (service) {
      this.$emit('add', service)
    },
    handleRemove (service) {
      this.$emit('remove', service)
    },
    open () {
      if (this.disabled) return
      this.$refs.modal?.open()
    },
    close () {
      this.$refs.modal?.close()
    },
    clearQuery () {
      this.query = ''
      this.$refs.search?.focus()
    },
    handleOpen () {
      // Reset the search + session counter each time the picker opens, and
      // focus the search field so the user can start typing immediately.
      // We retry for a few frames because the modal mounts inside a portal +
      // transition, so the input may not exist on the very next tick.
      this.query = ''

      const focusSearch = (attempt = 0) => {
        const el = this.$refs.search
        if (el) {
          el.focus()
          return
        }
        if (attempt < 10) {
          requestAnimationFrame(() => focusSearch(attempt + 1))
        }
      }
      this.$nextTick(() => focusSearch())
    },
  },
}
</script>
